import React from 'react';
import { useTranslation } from 'react-i18next';

import Badge from 'component/Incentive/Badge';
import Checkbox from 'component/Layout/Form/Checkbox';
import Tooltip from 'component/Layout/Tooltip';
import { TooltipPosition } from 'component/Layout/Tooltip/types';

import classNames from 'classnames';

import { ConditionalWrapper } from 'utility/Check/ConditionalWrapper';
import { isTablet } from 'utility/Check/Device';
import { getDistanceFormat } from 'utility/Format/Distance';

import { Iata } from 'types/Search/airport';

import { ItemType } from '../types';

import styles from './Airport.module.scss';

interface AirportProps {
    checkbox: {
        name: string;
        id: string;
        value: Iata;
    };
    label: string;
    iataCode: Iata;
    onChange: (entry: Iata) => void;
    isChecked?: boolean;
    distance?: number;
    badge?: ItemType;
    firstAirport?: string;
}

const badgeMap = {
    ['price']: 'TRANS-price-tip',
    ['popular']: 'TRANS-popular',
};
const Airport = ({
    checkbox,
    label,
    iataCode,
    onChange,
    isChecked = false,
    distance,
    badge,
    firstAirport,
}: AirportProps) => {
    const { t } = useTranslation();
    const checkboxKey = `${iataCode}-${isChecked}`;

    return (
        <Checkbox
            key={checkboxKey}
            input={checkbox}
            customClass={{
                Label: styles.ListItemContent,
                Wrapper: classNames(styles.ListItem, {
                    [styles.Tablet]: isTablet(),
                }),
            }}
            onChange={() => {
                onChange?.(checkbox.value);
            }}
            isChecked={isChecked}
        >
            <span className={styles.Airport}>{t(label)}</span>
            <span className={styles.IataCode}>{iataCode}</span>
            {distance !== undefined && (
                <span className={styles.IataCode}>
                    {getDistanceFormat(distance, true)}
                </span>
            )}
            {(badge === ItemType.Price || badge === ItemType.Popular) && (
                <ConditionalWrapper
                    condition={badge === ItemType.Price && !!firstAirport}
                    wrapper={(children: React.ReactElement) => (
                        <Tooltip
                            content={
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t('TRANS-airport-pricetip', {
                                            priceTipAirport: t(label),
                                            airport: firstAirport,
                                        }),
                                    }}
                                />
                            }
                            placement={TooltipPosition.Top}
                            stopPropagationForTooltip
                            stopPropagationForChildren
                        >
                            {children}
                        </Tooltip>
                    )}
                >
                    <Badge
                        customClass={classNames(styles.Badge, {
                            [styles.Price]: badge === ItemType.Price,
                            [styles.Popular]: badge === ItemType.Popular,
                        })}
                    >
                        {t(badgeMap[badge])}
                    </Badge>
                </ConditionalWrapper>
            )}
        </Checkbox>
    );
};

export default Airport;
